import React from "react";

import { AuthenticatedUserModel } from "@courses-utils/gateway-auth";
import { pm2ConfigStore, makePm2ConfigStore } from "@pm2/store";
import { GlossaryCollectionComponent } from "@pm2/glossary-collection-component";
import { UIKitNotifierProvider } from "@ui/kit";

import { QueryClient, QueryCache } from "@tanstack/react-query";

import {
  AccordionModel,
  AudioModel,
  ElementLinkModel,
  ElementModel,
  Engine,
  GlossaryWordModel,
  GridModel,
  IframeModel,
  ImageModel,
  InternalLinkModel,
  RichtextModel,
  SliderModel,
  VideoModel,
  CallToActionCardsModel,
  SplitModel,
  GlossaryCollectionModel,
} from "@courses-pm2/api";
import {
  DAYS_TO_MS,
  HOURS_TO_MS,
  isPlatformNodeJS,
  MINUTES_TO_MS,
  resets,
  units,
} from "@pm2/compat";

import { css, cx } from "@linaria/core";
import { PM2NavigationProvider } from "../components/provider.navigation.client";

const tanstackQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: isPlatformNodeJS() ? 10 * MINUTES_TO_MS : 1 * HOURS_TO_MS,
      gcTime: isPlatformNodeJS() ? 15 * MINUTES_TO_MS : 3 * DAYS_TO_MS,
    },
  },
  queryCache: new QueryCache({
    onError: (err) => console.error(err),
  }),
});

/**
 * Shorthand typing of the constructor of an element model.
 */
type ElementModelCtor = new (...args: unknown[]) => ElementModel;

/**
 * Specification of props available when calling out to engine.render, which
 * provides us with a mechanism to inject the desired custom props.
 */
export type RendererProps = {
  inLightbox?: boolean;
};

/**
 * Specification of the arguments that'll be given to the renderer methods
 * returned by the mapper, when the engine prompts them to render a model
 * instance.
 */
type RendererArgs<M extends ElementModel = ElementModel> = {
  model: M;
  engine: Engine<RendererProps>;
  props: Partial<RendererProps>;
};

/**
 * Specification of the signature for the renderer methods that are returned by
 * the mapper method.
 */
type Renderer<M extends ElementModel> = (
  args: RendererArgs<M>,
) => React.ReactNode;

const glossaryCollectionWrapperCls = css`
  max-width: ${units.px(700)};
  margin-inline: auto;
  margin-block: ${units.px(80)};
`;

/**
 * We're using this mapper to post-load PM2 components as they are being used
 * by the application.
 */
export async function mapPm2Components(
  modelType: ElementModelCtor,
  args: {
    user?: AuthenticatedUserModel;
    // tslint:disable completed-docs
    openLightbox(args: { model: ElementModel; node: HTMLElement }): void;
    openInternalLink(args: { path: string; portalId: string }): void;
    onSliderDragStart(): void;
    onSliderDragEnd(): void;
    // tslint:enable completed-docs
  },
  // tslint:disable-next-line: no-any
): Promise<Renderer<any> | undefined> {
  switch (modelType) {
    case AccordionModel:
      const { AccordionComponent } =
        await import("@courses-pm2/accordion-component");

      return ({ model }: RendererArgs<AccordionModel>) => (
        <AccordionComponent model={model} />
      );

    case AudioModel:
      const { AudioComponent } = await import("@courses-pm2/audio-component");

      return ({ model }: RendererArgs<AudioModel>) => (
        <AudioComponent
          caption={model.caption}
          isScrambled={model.isScrambled}
          src={model.src}
          imageSrc={model.imageSrc}
          title={model.title ?? ""}
          transcript={model.transcript}
        />
      );

    case GridModel:
      const { GridComponent } = await import("@courses-pm2/grid-component");

      return ({ model }: RendererArgs<GridModel>) => (
        <GridComponent model={model} />
      );

    case IframeModel:
      const { IframeComponent } = await import("@courses-pm2/iframe-component");
      const userId = args.user ? args.user.id : undefined;
      const institutionId = args.user
        ? ((await args.user.getInstitutionId("")) ?? undefined)
        : undefined;

      return ({ model, props }: RendererArgs<IframeModel>) => (
        <IframeComponent
          model={model}
          userId={userId}
          institutionId={institutionId}
          inLightbox={props.inLightbox}
          openLightbox={args.openLightbox}
        />
      );

    case ImageModel:
      const { ImageComponent } = await import("@courses-pm2/image-component");

      return ({ model, props }: RendererArgs<ImageModel>) => (
        <ImageComponent
          model={model}
          inLightbox={props.inLightbox}
          openLightbox={args.openLightbox}
          openInternalLink={args.openInternalLink}
        />
      );

    case RichtextModel:
      const { RichtextComponent } =
        await import("@courses-pm2/richtext-component");

      return ({ model }: RendererArgs<RichtextModel>) => (
        <RichtextComponent model={model} />
      );

    case SliderModel:
      const { SliderComponent } = await import("@courses-pm2/slider-component");

      return ({ model, engine }: RendererArgs<SliderModel>) => (
        <SliderComponent
          model={model}
          onDragStart={args.onSliderDragStart}
          onDragEnd={args.onSliderDragEnd}
        />
      );

    case SplitModel:
      const { SplitComponent } = await import("@courses-pm2/split-component");

      return ({ model, props }: RendererArgs<SplitModel>) => (
        <SplitComponent
          model={model}
          inLightbox={props.inLightbox}
          openLightbox={args.openLightbox}
          openInternalLink={args.openInternalLink}
        />
      );

    case GlossaryCollectionModel:
      return ({ model }: RendererArgs<GlossaryCollectionModel>) => {
        const store = makePm2ConfigStore({
          system: "portal",
          baseUrl: "/",
          host: "",
          previewMode: false,
          config: {
            id: model.portalId,
            description: "",
            homePath: "",
            searchPath: "",
            siteType: "portal",
            title: "",
            uniqueId: "",
            // @ts-expect-error Only partial definition of theme required
            theme: {
              targetAudience: "Website",
              favIconImage: undefined,
              productLogoImage: undefined,
              componentPreviewImage: undefined,
            },
            settings: {
              enableGeoGebra: true,
              enableSleeknote: true,
              enableTableOfContentsForCourses: true,
              footerColumns: [],
              loginDisabled: false,
              loginFreeAccess: false,
              loginProvider: "gu-all",
              ordbogenDotComEnabled: false,
              showCourseFrontpages: true,
              showCoursePlanner: true,
              accessKeys: undefined,
            },
          },
        });

        return (
          <pm2ConfigStore.Provider store={store}>
            <PM2NavigationProvider>
              <UIKitNotifierProvider>
                <div
                  className={cx(resets.rootCls, glossaryCollectionWrapperCls)}
                >
                  <GlossaryCollectionComponent
                    node={{
                      nodeType: "glossary-collection-component",
                      id: model.learningObject?.id ?? "",
                      title: model.title,
                      description: model.description,
                      glossaryWords: model.glossaryWords,
                      options: model.options,
                      pm2: {
                        enableLightboxButton: model.toolbar.enableLightbox,
                        enableNewWindowButton: model.toolbar.enableNewWindow,
                        enableNotebookButton: model.toolbar.enableNotebook,
                        isScrambled: model.isScrambled,
                        learningObjectId: model.learningObject?.id,
                        learningObjectTitle: model.learningObject?.title,
                        pdfExport: undefined,
                        pdfFileUrl: undefined,
                        ribbonIconId: model.ribbon,
                        speechFileUrl: undefined,
                        transcript: undefined,
                      },
                    }}
                  />
                </div>
              </UIKitNotifierProvider>
            </PM2NavigationProvider>
          </pm2ConfigStore.Provider>
        );
      };

    case VideoModel:
      const { VideoComponent } = await import("@courses-pm2/video-component");

      return ({ model }: RendererArgs<VideoModel>) => (
        <VideoComponent model={model} />
      );

    case InternalLinkModel:
      const { InternalLink } =
        await import("@apps/viewer/src/ts/components/pm2");

      return ({ model }: RendererArgs<InternalLinkModel>) => (
        <InternalLink model={model} />
      );

    case ElementLinkModel:
      const { ElementLink } =
        await import("@apps/viewer/src/ts/components/pm2");

      return ({ model }: RendererArgs<ElementLinkModel>) => (
        <ElementLink model={model} />
      );

    case GlossaryWordModel:
      const { GlossaryWord } =
        await import("@apps/viewer/src/ts/components/pm2");

      return ({ model }: RendererArgs<GlossaryWordModel>) => (
        <GlossaryWord model={model} />
      );

    case CallToActionCardsModel:
      const { CallToActionComponent } =
        await import("@courses-pm2/cta-cards-component");

      return ({ model, engine }: RendererArgs<CallToActionCardsModel>) => (
        <CallToActionComponent
          portalId={model.portalId}
          title={model.title}
          cards={model.cards}
          isScrambled={model.isScrambled}
        />
      );

    default:
      // If we get here, the model type isn't supported, bail out...
      return undefined;
  }
}
